import { useEffect, useState } from "react"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useQuery } from "@tanstack/react-query"
import { api } from "@/lib/api"
import { auth } from "@/lib/auth"
import { setLocale, useT, type Locale } from "@/lib/i18n"
import { Button, Card, CardBody, Input, Spinner, Toast } from "@/components/ui"
import type { ConnectorOut, TelegramLinkOut } from "@/lib/types"
import { CheckIcon, MailIcon, SendIcon, SparklesIcon } from "lucide-react"

export const Route = createFileRoute("/_app/welcome")({
  component: WelcomePage,
})

type ReplyLang = "mirror" | "el" | "en"

const STARTERS: Record<Locale, string[]> = {
  el: ["Σχεδίασε τη μέρα μου", "Τι έχω στο ημερολόγιο αύριο;", "Γράψε μου ένα email", "Φτιάξε μου μια εικόνα"],
  en: ["Plan my day", "What's on my calendar tomorrow?", "Draft an email for me", "Make me an image"],
}

function WelcomePage() {
  const t = useT()
  const nav = useNavigate()
  const me = auth.me()
  const [step, setStep] = useState(0)
  const [name, setName] = useState(me?.display_name ?? "")
  const [ui, setUi] = useState<Locale>(me?.ui_locale ?? (navigator.language.toLowerCase().startsWith("el") ? "el" : "en"))
  const [reply, setReply] = useState<ReplyLang>(me?.reply_language ?? "mirror")
  const [tz, setTz] = useState(me?.tz && me.tz !== "UTC" ? me.tz : (Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"))
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  // Live-preview the interface language while the user picks.
  useEffect(() => { setLocale(ui) }, [ui])

  async function saveProfile(finish: boolean) {
    setBusy(true); setErr(null)
    try {
      await api("/api/me/profile", {
        method: "PATCH",
        body: { display_name: name.trim() || null, tz, ui_locale: ui, reply_language: reply, onboarded: finish },
      })
      await auth.refreshMe()
      return true
    } catch (e) {
      setErr((e as { detail?: string }).detail || "Could not save.")
      return false
    } finally {
      setBusy(false)
    }
  }

  async function next() {
    if (step === 0 && !(await saveProfile(false))) return
    setStep((s) => Math.min(2, s + 1))
  }

  async function finish(starter?: string) {
    if (!(await saveProfile(true))) return
    await nav({ to: "/chat", search: starter ? { c: undefined, q: starter } : {} })
  }

  return (
    <div className="mx-auto max-w-xl py-4 md:py-10">
      <div className="mb-6 text-center">
        <img src="/lucy-logo.png" alt="" className="mx-auto mb-3 h-14 w-14 rounded-full object-cover shadow-[0_4px_18px_rgba(192,140,255,0.5)]" />
        <h1 className="text-2xl font-semibold tracking-tight">{t("welcome.title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("welcome.subtitle")}</p>
      </div>

      <ol className="mb-5 flex items-center justify-center gap-2 text-[11px] uppercase tracking-wider">
        {[t("welcome.step1"), t("welcome.step2"), t("welcome.step3")].map((label, i) => (
          <li key={label} className={`flex items-center gap-2 ${i === step ? "text-foreground" : "text-muted-foreground"}`}>
            <span className={`flex h-5 w-5 items-center justify-center rounded-full border text-[10px] ${i < step ? "border-primary bg-primary text-primary-foreground" : i === step ? "border-primary text-primary" : "border-border"}`}>
              {i < step ? <CheckIcon className="h-3 w-3" /> : i + 1}
            </span>
            {label}
            {i < 2 && <span className="mx-1 h-px w-6 bg-white/10" />}
          </li>
        ))}
      </ol>

      <Card>
        <CardBody className="space-y-4">
          {step === 0 && (
            <>
              <Field label={t("welcome.nameLabel")}>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("welcome.namePh")} autoFocus />
              </Field>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label={t("lang.ui")}>
                  <Select value={ui} onChange={(v) => setUi(v as Locale)} options={[["el", t("lang.el")], ["en", t("lang.en")]]} />
                </Field>
                <Field label={t("lang.reply")}>
                  <Select value={reply} onChange={(v) => setReply(v as ReplyLang)} options={[["mirror", t("lang.mirror")], ["el", t("lang.el")], ["en", t("lang.en")]]} />
                </Field>
              </div>
              <Field label={t("account.timezone")}>
                <Input value={tz} onChange={(e) => setTz(e.target.value)} className="font-mono" />
              </Field>
            </>
          )}

          {step === 1 && <ConnectStep />}

          {step === 2 && (
            <div className="text-center">
              <SparklesIcon className="mx-auto mb-2 h-6 w-6 text-primary" />
              <h2 className="text-lg font-semibold">{t("welcome.readyTitle")}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{t("welcome.readyBody")}</p>
              <div className="mt-5 flex flex-wrap justify-center gap-2">
                {STARTERS[ui].map((s) => (
                  <button
                    key={s}
                    disabled={busy}
                    onClick={() => void finish(s)}
                    className="rounded-[11px] border border-border bg-foreground/[0.04] px-3.5 py-2.5 text-[13px] text-foreground/85 transition hover:border-primary/30 hover:bg-foreground/[0.06]"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {err && <Toast variant="destructive">{err}</Toast>}

          <div className="flex items-center justify-between pt-2">
            <Button variant="ghost" disabled={busy || step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))}>
              {t("welcome.back")}
            </Button>
            <div className="flex items-center gap-2">
              {step === 1 && (
                <Button variant="ghost" disabled={busy} onClick={() => setStep(2)}>{t("welcome.skip")}</Button>
              )}
              {step < 2 ? (
                <Button disabled={busy} onClick={() => void next()}>{busy ? <Spinner /> : null}{t("welcome.next")}</Button>
              ) : (
                <Button disabled={busy} onClick={() => void finish()}>{busy ? <Spinner /> : null}{t("welcome.start")}</Button>
              )}
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  )
}

function ConnectStep() {
  const t = useT()
  const me = auth.me()
  const [busy, setBusy] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const connectors = useQuery({
    queryKey: ["connectors", me?.id],
    queryFn: () => api<ConnectorOut[]>("/api/dashboard/connectors", { query: { user_id: me?.id } }),
    enabled: !!me?.id,
    refetchInterval: 5_000,
  })
  const has = (kind: string) => (connectors.data ?? []).some((c) => c.kind === kind || c.kind.startsWith(kind))

  async function google() {
    setBusy("google"); setErr(null)
    try {
      const out = await api<{ url: string }>("/api/dashboard/connectors/gmail/oauth/start", { method: "POST", query: { user_id: me?.id } })
      window.location.href = out.url
    } catch (e) {
      setErr((e as { detail?: string }).detail || "Google sign-in isn't available right now.")
      setBusy(null)
    }
  }

  async function telegram() {
    setBusy("telegram"); setErr(null)
    try {
      const out = await api<TelegramLinkOut>("/api/dashboard/connectors/telegram/start-link", { method: "POST", query: { user_id: me?.id } })
      window.open(out.deep_link, "_blank", "noopener,noreferrer")
    } catch (e) {
      setErr((e as { detail?: string }).detail || "Telegram linking isn't available right now.")
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">{t("welcome.connectIntro")}</p>
      <ConnectRow icon={<MailIcon className="h-4 w-4" />} title={t("welcome.google")} hint={t("welcome.googleHint")}
        connected={has("gmail") || has("gcal") || has("google")} busy={busy === "google"} onConnect={google} />
      <ConnectRow icon={<SendIcon className="h-4 w-4" />} title={t("welcome.telegram")} hint={t("welcome.telegramHint")}
        connected={has("telegram")} busy={busy === "telegram"} onConnect={telegram} />
      {err && <Toast variant="destructive">{err}</Toast>}
    </div>
  )
}

function ConnectRow({ icon, title, hint, connected, busy, onConnect }: {
  icon: React.ReactNode; title: string; hint: string; connected: boolean; busy: boolean; onConnect: () => void
}) {
  const t = useT()
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border bg-foreground/[0.04] p-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">{icon}</span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium">{title}</div>
        <div className="text-xs text-muted-foreground">{hint}</div>
      </div>
      {connected ? (
        <span className="inline-flex items-center gap-1 text-xs text-[hsl(var(--ok))]"><CheckIcon className="h-3.5 w-3.5" />{t("welcome.connected")}</span>
      ) : (
        <Button size="sm" variant="outline" disabled={busy} onClick={onConnect}>{busy ? <Spinner /> : null}{t("welcome.connect")}</Button>
      )}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</label>
      {children}
    </div>
  )
}

function Select({ value, options, onChange }: { value: string; options: [string, string][]; onChange: (v: string) => void }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-xl border border-border bg-black/25 px-3.5 py-2.5 text-sm text-foreground focus-visible:outline-none focus-visible:border-primary/50 [&>option]:bg-card"
    >
      {options.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
    </select>
  )
}
